import React, { useEffect, useCallback, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Image,
  Alert,
  Container,
  Button,
  Modal,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SubtleBadge from "components/common/SubtleBadge";
import useFormattedDate from "hooks/useFormatDate";
import useTasks from "hooks/tasks/useTasks";
import DeactivateTaskModal from "./DeactivateTaskModal";
import { toast } from "react-toastify";
import Guard from "components/access-control/rbac/Guard";
import { PRIV } from "components/access-control/rbac/privileges";
import CopyableText from "components/common/CopyableText";
import { Spin, Tag, Button as AntButton } from "antd";
import { getTagStyle } from "components/finance/charges/ChargeConfigsTable";
import DOMPurify from "dompurify";
import { useLocation } from "react-router-dom";
import paths from "routes/paths";
import { STORAGE_BASE_URL } from "constants";
import {
  CompensationCard,
  InterestedHelpersCard,
  ProfileReviewsDrawer,
  ProgressPingsDrawer,
  TaskReviewsDrawer,
} from "./TaskDetailDrawers";

const TaskDetailsPage = () => {
  const { getTaskById, deactivateTask } = useTasks();
  const { taskId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatDate } = useFormattedDate();
  const [showModal, setShowModal] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [mapModal, setMapModal] = useState({
    open: false,
    title: "",
    latitude: null,
    longitude: null,
  });
  const returnState = location.state || {};
  const returnStateStatus = returnState.status || "";
  const returnStatePage = returnState.page;
  const returnStatePageSize = returnState.pageSize;
  const returnStateBusinessEntityId = returnState.businessEntityId || "";
  const returnToTasksPath = useMemo(() => {
    const returnParams = new URLSearchParams();

    if (returnStateStatus) {
      returnParams.set("status", returnStateStatus);
    }
    if (returnStatePage !== undefined && returnStatePage !== null) {
      returnParams.set("page", String(returnStatePage));
    }
    if (returnStatePageSize !== undefined && returnStatePageSize !== null) {
      returnParams.set("size", String(returnStatePageSize));
    }
    if (returnStateBusinessEntityId) {
      returnParams.set("businessEntityId", returnStateBusinessEntityId);
    }

    const query = returnParams.toString();
    return query ? `${paths.tasks}?${query}` : paths.tasks;
  }, [
    returnStateBusinessEntityId,
    returnStatePage,
    returnStatePageSize,
    returnStateStatus,
  ]);
 
  useEffect(() => {
    if (location.state?.openDrawer) {
      setActiveDrawer(location.state.openDrawer);
    }
  }, [location.state]);

  // Updated triggerToast to handle both message and variant
  const triggerToast = useCallback((message, variant = "success") => {
    const toastMessage =
      message ||
      (variant === "success" ? "Operation successful" : "Something went wrong");
    toast(toastMessage, {
      type: variant,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      autoClose: 3500,
      hideProgressBar: false,
      closeButton: true,
    });
  }, []);
  // Fetch task on mount or taskId change
  useEffect(() => {
    if (!taskId) return;

    const loadTask = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTaskById(taskId);
        console.log("Task", data);
        setTask(data);
      } catch (err) {
        setError("Failed to load task details");
      } finally {
        setLoading(false);
      }
    };

    loadTask();
  }, [taskId, getTaskById]);

  const openTaskReviews = () => setActiveDrawer("taskReviews");

  const openRequesterReviews = () => {
    if (!task?.requester?.id) return;
    setActiveDrawer("requesterReviews");
  };

  const handleDeactivateTask = async () => {
    setProcessing(true);
    try {
      await deactivateTask(taskId);
      setProcessing(false);
      setShowModal(false);
      triggerToast("Task deactivated successfully", "success");

      navigate(-1);
    } catch (err) {
      triggerToast(err?.message || "Failed to deactivate task", "error");
      setProcessing(false);
    }
  };

  const openHelperReviews = () => {
    if (!task?.helper?.id) return;
    setActiveDrawer("helperReviews");
  };

  const openProgressPings = () => setActiveDrawer("progressPings");
  const openCounterOffers = () => navigate(paths.taskCounterOffers(task.id));

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "ACCEPTED":
        return "success";
      case "PENDING":
        return "warning";
      case "IN_PROGRESS":
        return "info";
      case "COMPLETED":
        return "primary";
      case "CANCELLED":
        return "danger";
      default:
        return "secondary";
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const normalizeTaskPhoto = (photo, index) => {
    if (!photo) return null;

    const fileId =
      typeof photo === "object"
        ? photo.fileId || photo.file_id || photo.id || photo.file?.fileId || photo.file?.id || null
        : null;

    const url =
      typeof photo === "string"
        ? photo
        : photo?.url ||
          photo?.signedUrl ||
          photo?.fileUrl ||
          photo?.downloadUrl ||
          photo?.uri ||
          photo?.file?.url ||
          photo?.file?.signedUrl ||
          (fileId ? `${STORAGE_BASE_URL}/files/${fileId}` : "");

    if (!url) return null;

    return {
      key: fileId || `${index}-${url}`,
      url,
      label:
        photo?.caption ||
        photo?.name ||
        photo?.fileName ||
        photo?.filename ||
        `Photo ${index + 1}`,
      fileId,
    };
  };

  const hasValue = (value) => value !== null && value !== undefined && value !== "";

  const getMapEmbedUrl = (latitude, longitude) => {
    if (latitude === null || latitude === undefined) return null;
    if (longitude === null || longitude === undefined) return null;

    const lat = Number(latitude);
    const lng = Number(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;

    const delta = 0.01;
    const bbox = [
      lng - delta,
      lat - delta,
      lng + delta,
      lat + delta,
    ].join("%2C");

    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lng}`;
  };

  const openMapModal = (title, latitude, longitude) => {
    setMapModal({
      open: true,
      title: title || "Map",
      latitude,
      longitude,
    });
  };

  const closeMapModal = () => {
    setMapModal((prev) => ({ ...prev, open: false }));
  };

  const renderMapPreview = (title, latitude, longitude) => {
    const mapsUrl = getMapEmbedUrl(latitude, longitude);
    if (!mapsUrl) return null;

    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => openMapModal(title, latitude, longitude)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openMapModal(title, latitude, longitude);
          }
        }}
        className="position-relative rounded-4 overflow-hidden"
        style={{ height: 140, cursor: "pointer", background: "#f8f9fa" }}
      >
        <iframe
          title={`${title} preview`}
          src={mapsUrl}
          width="100%"
          height="100%"
          style={{ border: 0, pointerEvents: "none" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        <div
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end"
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.22) 100%)",
          }}
        >
          <div className="w-100 px-3 py-2 d-flex justify-content-between align-items-center">
            <span className="badge text-bg-light text-dark fw-semibold">
              Tap to expand
            </span>
            <FontAwesomeIcon icon="location-dot" className="text-white" />
          </div>
        </div>
      </div>
    );
  };

  const taskPhotos = useMemo(
    () => (Array.isArray(task?.photos) ? task.photos.map(normalizeTaskPhoto).filter(Boolean) : []),
    [task?.photos],
  );

  const categoryTags = useMemo(
    () =>
      Array.isArray(task?.categories)
        ? task.categories.filter(Boolean).map((category, index) => ({
            key: category?.id || `${category?.name || "category"}-${index}`,
            label: category?.name || category?.code || `Category ${index + 1}`,
            description: category?.description || "",
          }))
        : [],
    [task?.categories],
  );

  const renderEntityCard = (title, entity) => {
    if (!entity) return null;
    const handleOpenEntityDetails = () => {
      if (!entity?.id) return;
      navigate(paths.organizationDetails(entity.id), {
        state: { entity },
      });
    };

    const fields = [
      { label: "Name", value: entity?.name },
      { label: "Type", value: entity?.type },
      {
        label: "Status",
        value: entity?.status ? <Tag color="default">{entity.status}</Tag> : null,
      },
      { label: "Email", value: entity?.email },
      { label: "Phone", value: entity?.phone },
      {
        label: "ID",
        value: entity?.id ? <CopyableText value={entity.id} /> : null,
      },
    ].filter((field) => hasValue(field.value));

    return (
      <Card className="mb-3 p-3 pb-4 shadow-sm border-0 rounded-4">
        <div className="d-flex align-items-start justify-content-between gap-2">
          <h6 className="fw-bold mb-0">
            <FontAwesomeIcon icon="building" className="me-2" />
            {title}
          </h6>
          {entity?.id ? (
            <Button
              variant="outline-secondary"
              size="sm"
              className="text-xs"
              onClick={handleOpenEntityDetails}
            >
              View Details
            </Button>
          ) : null}
        </div>
        <div className="mt-3">
          <Row className="g-3">
            {fields.map((field) => (
              <Col xs={12} md={field.label === "ID" ? 12 : 6} key={field.label}>
                <div>
                  <div className="text-xxs text-muted fw-semibold mb-1">{field.label}</div>
                  <div className="fw-semibold text-sm">{field.value}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spin />
      </Container>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center my-5">
        {error}
      </Alert>
    );
  }

  if (!task) {
    return <p className="text-center my-5">No task found.</p>;
  }

  return (
    <>
      <div className="my-3">
        <Card className="mb-3 rounded-3 shadow-sm border-0">
          <Card.Body className="d-flex align-items-center justify-content-between py-2 px-3">
            <AntButton
              type="default"
              onClick={() => navigate(returnToTasksPath, { replace: true })}
            >
              Go Back
            </AntButton>

            <h4 className="mb-0 fw-semibold text-sm text-center flex-grow-1">
              Task Details
            </h4>

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={openTaskReviews}
              className="text-xs rounded-pill px-3 py-1"
            >
              <FontAwesomeIcon icon="star" className="me-2" />
              Task Reviews
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={openProgressPings}
              className="text-xs rounded-pill px-3 py-1 ms-2"
            >
              <FontAwesomeIcon icon="route" className="me-2" />
              Progress & Pings
            </Button>
          </Card.Body>
         
        </Card>

        <Row className="g-3 align-items-start">
          {/* Left Main Content */}
          <Col lg={7} xl={7}>
            <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="info-circle" className="me-2" />
                Basic Information
              </h6>
              <div className="px-3 mt-2">
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <p className="fw-semibold text-xxs mb-0">Title:</p>
                      <h6 className="mb-0 fw-semibold text-sm">{task.title}</h6>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Description:
                      </strong>
                      <div
                        className="mb-0 fw-semibold text-sm"
                        style={{ whiteSpace: "normal" }}
                      >
                        {task.description ? (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: DOMPurify.sanitize(task.description),
                            }}
                          />
                        ) : (
                          "No description provided"
                        )}
                      </div>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Status:
                      </strong>
                      <div>
                        {hasValue(task.status) ? (
                          <SubtleBadge
                            pill
                            bg={getStatusBadgeVariant(task.status)}
                            className="me-2"
                          >
                            {task.status}
                          </SubtleBadge>
                        ) : null}
                        {hasValue(task.giveTip) ? (
                          <SubtleBadge pill bg="warning">
                            Tip: {task.giveTip}
                          </SubtleBadge>
                        ) : null}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Task ID:
                      </strong>
                      <h6 className="mb-0 font-monospace fw-semibold text-sm">
                        <CopyableText value={task.id} />
                      </h6>
                    </div>
                    <div className="mb-3">
                      <strong className="fw-semibold text-xxs mb-0">
                        Duration:
                      </strong>
                      <h6 className="mb-0 fw-semibold text-sm">
                        Requested: {task.requesterDuration}
                        {task.actualDuration && (
                          <>
                            <br />
                            Actual: {task.actualDuration}
                          </>
                        )}
                      </h6>
                    </div>
                    {hasValue(task.rating) ? (
                      <div className="mb-3">
                        <strong className="fw-semibold text-xxs mb-0">
                          Rating:
                        </strong>
                        <h6 className="mb-0 fw-semibold text-sm">
                          {`${task.rating}/5 ⭐`}
                        </h6>
                      </div>
                    ) : null}
                  </Col>
                </Row>
              </div>
            </Card>

            {categoryTags.length > 0 && (
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="tags" className="me-2" />
                  Categories
                </h6>
                <div className="px-3 mt-2">
                  <div className="d-flex flex-wrap gap-2">
                    {categoryTags.map((category) => (
                      <Tag key={category.key} color="blue">
                        {category.label}
                      </Tag>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="clipboard-list" className="me-2" />
                Task Metadata
              </h6>
              <div className="px-3 mt-2">
                <Row className="g-3">
                  {task.taskType && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Task Type</div>
                        <div className="fw-semibold text-sm">{task.taskType}</div>
                      </div>
                    </Col>
                  )}
                  {hasValue(task.giveTip) && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Give Tip</div>
                        <div>
                          {typeof task.giveTip === "boolean" ? (
                            <Tag color="gold">{task.giveTip ? "Yes" : "No"}</Tag>
                          ) : (
                            <Tag color="gold">{task.giveTip}</Tag>
                          )}
                        </div>
                      </div>
                    </Col>
                  )}
                  {task.conversationId && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Conversation ID</div>
                        <div className="fw-semibold text-sm">
                          <CopyableText value={task.conversationId} />
                        </div>
                      </div>
                    </Col>
                  )}
                  {task.isResubmitted !== null && task.isResubmitted !== undefined && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Resubmitted</div>
                        <div>{task.isResubmitted ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}</div>
                      </div>
                    </Col>
                  )}
                  {task.requesterBusinessEntityId && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Requester Organization ID</div>
                        <div className="fw-semibold text-sm">
                          <CopyableText value={task.requesterBusinessEntityId} />
                        </div>
                      </div>
                    </Col>
                  )}
                  {task.helperBusinessEntityId && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Helper Organization ID</div>
                        <div className="fw-semibold text-sm">
                          <CopyableText value={task.helperBusinessEntityId} />
                        </div>
                      </div>
                    </Col>
                  )}
                  {task.paymentChargeDetailsId && (
                    <Col xs={12} md={6}>
                      <div>
                        <div className="text-xxs text-muted fw-semibold mb-1">Payment Charge Details ID</div>
                        <div className="fw-semibold text-sm">
                          <CopyableText value={task.paymentChargeDetailsId} />
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            </Card>

            <CompensationCard compensation={task.compensation} />

            {/* Location Information */}
            {(hasValue(task.location?.address) ||
              hasValue(task.location?.latitude) ||
              hasValue(task.location?.longitude)) && (
              <Card className="mb-3 pb-3 shadow-sm border-0 rounded-4">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="map-marker-alt" className="me-2" />
                  Location
                </h6>
                <div className="px-3 mt-2">
                  <Row className="g-3">
                    {hasValue(task.location?.address) && (
                      <Col xs={12} md={8}>
                        <div>
                          <div className="text-xxs text-muted fw-semibold mb-1">
                            Address
                          </div>
                          <div className="fw-semibold text-sm">
                            {task.location.address}
                          </div>
                        </div>
                      </Col>
                    )}
                    {hasValue(task.location?.latitude) &&
                      hasValue(task.location?.longitude) && (
                        <Col xs={12} md={hasValue(task.location?.address) ? 4 : 12}>
                          <div>
                            <div className="text-xxs text-muted fw-semibold mb-1">
                              Coordinates
                            </div>
                            {renderMapPreview(
                              "Task Location",
                              task.location.latitude,
                              task.location.longitude,
                            )}
                            <div className="mt-2 fw-semibold text-sm text-muted">
                              {task.location.latitude}, {task.location.longitude}
                            </div>
                          </div>
                        </Col>
                      )}
                  </Row>
                </div>
              </Card>
            )}

            <InterestedHelpersCard interestedHelpers={task.interestedHelpers} />

            {/* Financial Information */}
            {task.taskCharges && (
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="dollar-sign" className="me-2" />
                  Financial Details
                </h6>
                <div className="px-3 mt-2">
                  <Row>
                    <Col md={6}>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Token Amount:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.tokenAmount,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Service Fee:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.serviceFee,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">State Tax:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.stateTax,
                          task.taskCharges.currency,
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Federal Tax:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.federalTax,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-dark">Tip Amount:</strong>{" "}
                        {formatCurrency(
                          task.taskCharges.tipAmount,
                          task.taskCharges.currency,
                        )}
                      </div>
                      <div className="mb-2 text-xs">
                        <strong className="text-primary">Total Charge:</strong>
                        <span className="text-primary fw-bold">
                          {" "}
                          {formatCurrency(
                            task.taskCharges.totalCharge,
                            task.taskCharges.currency,
                          )}
                        </span>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            )}

            {/* Charge Breakdown */}
            {task.taskCharges?.chargeLines?.length > 0 && (
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
                <h6 className="mt-3 px-3 fw-bold">
                  <FontAwesomeIcon icon="receipt" className="me-2" />
                  Charge Breakdown
                </h6>

                <div className="px-3 mt-2">
                  {task.taskCharges.chargeLines
                    .sort((a, b) => a.displayOrder - b.displayOrder)
                    .map((charge, index, arr) => {
                      const isLast = index === arr.length - 1;

                      return (
                        <div
                          key={charge.chargeId}
                          className={`d-flex justify-content-between align-items-start pb-2 ${
                            !isLast ? "border-bottom mb-2" : ""
                          }`}
                        >
                          <div>
                            <div className="fw-semibold text-sm">
                              {charge.name}
                            </div>

                            {charge.description && (
                              <div className="text-muted text-xs">
                                {charge.description}
                              </div>
                            )}

                            <div className="text-muted text-xs">
                              {charge.valueType === "PERCENTAGE" &&
                              charge.rate !== null
                                ? `${(charge.rate * 100).toFixed(2)}%`
                                : "Fixed charge"}
                            </div>
                          </div>

                          <div className="text-end">
                            <div
                              className={`fw-semibold text-sm ${
                                charge.amount < 0 ? "text-danger" : "text-dark"
                              }`}
                            >
                              {formatCurrency(
                                charge.amount,
                                task.taskCharges.currency,
                              )}
                            </div>

                            <div>
                              <span
                                style={getTagStyle("category", charge.category)}
                              >
                                {charge.category}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </Card>
            )}

            {/* Payment Details */}
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="wallet" className="me-2" />
                Payment Details
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Chargeable Amount:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.chargeableAmount,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Cash Portion:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.cashPortion,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Credits Applied:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.appliedCreditsTotal,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-sm">
                  <strong>Uses Credits:</strong>{" "}
                  {task.taskCharges.useCredits ? "Yes" : "No"}
                </div>
              </div>
            </Card>

            {/* Platform & Processing Fees */}
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="coins" className="me-2" />
                Platform & Processing Fees
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Oliveeb Service Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.oliveebServiceFee,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Payment Processor Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.paymentProcessorFee,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Processor Fixed Fee:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.paymentFeeConstant,
                    task.taskCharges.currency,
                  )}
                </div>
              </div>
            </Card>

            {/* Settlement */}
              <Card className="mb-3 pb-2 shadow-sm border-0 rounded-4">
              <h6 className="mt-3 px-3 fw-bold">
                <FontAwesomeIcon icon="file-invoice-dollar" className="me-2" />
                Settlement
              </h6>
              <div className="px-3 mt-2">
                <div className="mb-2 text-xs">
                  <strong>Receivable Amount:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.receivableAmount,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Fee Tax Base:</strong>{" "}
                  {formatCurrency(
                    task.taskCharges.feeTaxBase,
                    task.taskCharges.currency,
                  )}
                </div>
                <div className="mb-2 text-xs">
                  <strong>Units:</strong> {task.taskCharges.units}
                </div>
              </div>
            </Card>

            {/* Photos large section */}
            {taskPhotos.length > 0 && (
              <Card className="mb-3 p-3 shadow-sm border-0 rounded-4">
                <h6 className="mb-0 fw-bold">
                  <FontAwesomeIcon icon="image" className="me-2" />
                  Photos ({taskPhotos.length})
                </h6>
                <Card.Body className="p-0 mt-2">
                  <Row className="g-3">
                    {taskPhotos.map((photo, index) => (
                      <Col key={photo.key} xs={12} sm={6} md={4}>
                            <Image
                              src={photo.url}
                              alt={photo.label}
                              fluid
                              rounded
                              style={{
                                width: "100%",
                                height: "250px",
                                objectFit: "cover",
                                cursor: "pointer",
                              }}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <div className="mt-2">
                          <p className="text-xs mb-0 text-muted">{photo.label}</p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            )}
          </Col>

          {/* Sidebar */}
          <Col lg={5} xl={5}>
            <div className="position-sticky" style={{ top: 24 }}>
            {/* Requester Information */}
            <Card className="mb-3 p-3 pb-4 shadow-sm border-0 rounded-4">
              <h6 className="fw-bold">
                <FontAwesomeIcon icon="user" className="me-2" />
                Requester
              </h6>
              <div className="mt-2">
                {task.requester ? (
                  <div className="d-grid gap-2">
                    {(hasValue(task.requester.firstName) ||
                      hasValue(task.requester.lastName)) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">Name:</p>
                        <h6 className="mb-0 fw-semibold text-sm text-end">
                          {[task.requester.firstName, task.requester.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </h6>
                      </div>
                    )}
                    {hasValue(task.requester.email) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">Email:</p>
                        <h6 className="mb-0 fw-semibold text-sm text-end">
                          {task.requester.email}
                        </h6>
                      </div>
                    )}
                    {hasValue(task.requester.id) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">ID:</p>
                        <h6 className="mb-0 fw-semibold text-sm font-monospace text-end">
                          <CopyableText value={task.requester.id} />
                        </h6>
                      </div>
                    )}
                    {hasValue(task.requesterBusinessEntityId) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">
                           Organization ID:
                        </p>
                        <h6 className="mb-0 fw-semibold text-sm font-monospace text-end">
                          <CopyableText value={task.requesterBusinessEntityId} />
                        </h6>
                      </div>
                    )}
                    <div className="d-grid">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={openRequesterReviews}
                        className="text-xs"
                      >
                        <FontAwesomeIcon icon="star" className="me-2" />
                        Profile Reviews
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">No requester information</p>
                )}
              </div>
            </Card>

            {renderEntityCard(
              "Requester Organization",
              task.requesterBusinessEntity,
            )}

            {/* Helper Information */}
            <Card className="mb-3 p-3 pb-4 shadow-sm border-0 rounded-4">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon icon="user-tie" className="me-2" />
                Helper
              </h6>
              <div className="mt-3">
                {task.helper ? (
                  <div className="d-grid gap-2">
                    {(hasValue(task.helper.firstName) ||
                      hasValue(task.helper.lastName)) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">Name:</p>
                        <h6 className="mb-0 fw-semibold text-sm text-end">
                          {[task.helper.firstName, task.helper.lastName]
                            .filter(Boolean)
                            .join(" ")}
                        </h6>
                      </div>
                    )}
                    {hasValue(task.helper.email) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">Email:</p>
                        <h6 className="mb-0 fw-semibold text-sm text-end">
                          {task.helper.email}
                        </h6>
                      </div>
                    )}
                    {hasValue(task.helper.id) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">ID:</p>
                        <h6 className="mb-0 fw-semibold text-sm font-monospace text-end">
                          <CopyableText value={task.helper.id} />
                        </h6>
                      </div>
                    )}
                    {hasValue(task.helperBusinessEntityId) && (
                      <div className="d-flex gap-2 justify-content-between">
                        <p className="fw-semibold text-xxs mb-0">
                           Organization ID:
                        </p>
                        <h6 className="mb-0 fw-semibold text-sm font-monospace text-end">
                          <CopyableText value={task.helperBusinessEntityId} />
                        </h6>
                      </div>
                    )}
                    <div className="d-grid">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={openHelperReviews}
                        className="text-xs"
                      >
                        <FontAwesomeIcon icon="star" className="me-2" />
                        Profile Reviews
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted">No helper assigned</p>
                )}
              </div>
            </Card>

            {renderEntityCard(
              "Helper Organization",
              task.helperBusinessEntity,
            )}

            {/* Timeline */}
            <Card className="mb-3 p-3 shadow-sm border-0 rounded-4">
              <h6 className="mb-0 fw-bold">
                <FontAwesomeIcon icon="clock" className="me-2" />
                Timeline
              </h6>
              <Card.Body className="p-0 mt-3">
                <div className="mb-2 d-flex gap-2">
                  <p className="fw-semibold text-xxs mb-0">Created:</p>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {formatDate(task.createdAt)}
                  </h6>
                </div>

                {task.helperAcceptedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Accepted:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperAcceptedAt)}
                    </h6>
                  </div>
                )}
                {task.helperArrivedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Arrived:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperArrivedAt)}
                    </h6>
                  </div>
                )}
                {task.helperCompletedAt && (
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-semibold text-xxs mb-0">Completed:</p>
                    <h6 className="mb-0 fw-semibold text-sm">
                      {formatDate(task.helperCompletedAt)}
                    </h6>
                  </div>
                )}
                <div className="mb-2 d-flex gap-2">
                  <p className="fw-semibold text-xxs mb-0">Last Updated:</p>
                  <h6 className="mb-0 fw-semibold text-sm">
                    {formatDate(task.updatedAt)}
                  </h6>
                </div>
              </Card.Body>
            </Card>

            <Card className="mb-3 p-3 shadow-sm border-0 rounded-4">
              <h6 className="fw-bold mb-2">
                <FontAwesomeIcon icon="exchange-alt" className="me-2" />
                Counter Offers
              </h6>

              <p className="text-xs text-muted mb-3">
                View and manage all negotiation offers for this task.
              </p>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-sm">Total Offers</span>
                <span className="fw-bold">
                  {task.counterOffers?.length || 0}
                </span>
              </div>

              <Button
                variant="outline-secondary"
                size="sm"
                className="w-100"
                onClick={openCounterOffers}
              >
                Open Counter Offers
              </Button>
            </Card>

            <div className="mt-3 mx-auto" style={{ maxWidth: 150 }}>
              <Guard privilege={PRIV.UPDATE_TASKS}>
                <Button
                  variant="danger"
                  onClick={() => setShowModal(true)}
                  size="sm"
                  className="w-100"
                >
                  Deactivate Task
                </Button>
              </Guard>
            </div>
            </div>
          </Col>
        </Row>
        <TaskReviewsDrawer
          show={activeDrawer === "taskReviews"}
          onHide={() => setActiveDrawer(null)}
          taskId={taskId}
        />
        <ProfileReviewsDrawer
          show={activeDrawer === "requesterReviews"}
          onHide={() => setActiveDrawer(null)}
          role="requester"
          userId={task?.requester?.id}
          userName={
            task?.requester
              ? [task.requester.firstName, task.requester.lastName].filter(Boolean).join(" ") ||
                task.requester.nickName ||
                "Requester"
              : "Requester"
          }
        />
        <ProfileReviewsDrawer
          show={activeDrawer === "helperReviews"}
          onHide={() => setActiveDrawer(null)}
          role="helper"
          userId={task?.helper?.id}
          userName={
            task?.helper
              ? [task.helper.firstName, task.helper.lastName].filter(Boolean).join(" ") ||
                task.helper.nickName ||
                "Helper"
              : "Helper"
          }
        />
        <ProgressPingsDrawer
          show={activeDrawer === "progressPings"}
          onHide={() => setActiveDrawer(null)}
          task={task}
          formatDate={formatDate}
        />
        <DeactivateTaskModal
          show={showModal}
          onClose={() => !processing && setShowModal(false)}
          onConfirm={handleDeactivateTask}
          processing={processing}
          task={{ id: taskId }} // just in case you want to pass task info later
        />

        <Modal
          show={mapModal.open}
          onHide={closeMapModal}
          centered
          size="lg"
          contentClassName="border-0"
        >
          <Modal.Header closeButton>
            <Modal.Title>{mapModal.title || "Map"}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-0">
            <div style={{ width: "100%", height: "70vh", background: "#f8f9fa" }}>
            {getMapEmbedUrl(mapModal.latitude, mapModal.longitude) ? (
              <iframe
                title={mapModal.title || "Map"}
                src={getMapEmbedUrl(
                  mapModal.latitude,
                  mapModal.longitude,
                )}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : (
                <div className="h-100 d-flex align-items-center justify-content-center text-muted">
                  No map available
                </div>
              )}
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default TaskDetailsPage;
